import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, Skeleton } from "antd";
import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { actions as routerActions } from "redux-router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import ModalFooter from "../../common/components/ModalFooter";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { routeNames } from "../common/routes.ts";
import { userManagementActions } from "../../features/user-management/userManagementSlice";
import { isAdministrator } from "../../common/utils/isAdministrator.ts";
import type {
	CurrentUser,
	User,
	UserGroup,
} from "../generated-axios-client/index.ts";
import { UserFields } from "./UserFields";

type NewUserPageProps = {
	dispatch: Dispatch;
	intl: IntlShape;
	initialValue?: User | null;
	availableGroups: UserGroup[];
	currentUser?: CurrentUser;
};

export function NewUserPage({
	dispatch,
	intl,
	initialValue,
	availableGroups,
	currentUser,
}: NewUserPageProps) {
	const [form] = Form.useForm();

	const isEdit = !!initialValue?.id;
	const isAdmin = isAdministrator(currentUser?.groups);

	useEffect(() => {
		if (currentUser && !isAdmin) {
			dispatch(routerActions.navigateTo(routeNames.SETTINGS_USER_LIST));
		}
	}, [currentUser, isAdmin, dispatch]);

	const save = () => {
		form
			.validateFields()
			.then((values) => {
				const { group, email, ...rest } = values;
				const payload = {
					...rest,
					groups: group != null ? [group] : [],
					...(isEdit ? { id: initialValue!.id } : {}),
					username: email,
					email: email,
				};
				dispatch(userManagementActions.addOrUpdateUser(payload));
			})
			.catch((errorInfo) => {
				console.error("Validation failed:", errorInfo);
			});
	};

	const initialValues = useMemo(() => {
		if (!initialValue) return undefined;
		const { groups, ...rest } = initialValue;
		return { ...rest, group: groups?.[0] ?? null };
	}, [initialValue]);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	if (!isAdmin || (isEdit && !initialValue)) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({
					id: isEdit ? "user_edit_title" : "user_create_title",
				})}
				subtitle={
					isEdit
						? undefined
						: intl.formatMessage({ id: "user_create_subtitle" })
				}
			>
				<Form form={form} layout="vertical" initialValues={initialValues}>
					<UserFields
						intl={intl}
						isEdit={isEdit}
						availableGroups={availableGroups}
					/>
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(routerActions.navigateTo(routeNames.SETTINGS_USER_LIST))
						}
					>
						{intl.formatMessage({ id: "ticket_button_title_cancel" })}
					</Button>
					<Button type="primary" onClick={save}>
						{intl.formatMessage({ id: "ticket_button_title_create" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const ConnectedNewUserPage = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	initialValue: state.userManagement.user,
	availableGroups: state.userManagement.availableGroups,
	currentUser: state.userstate.user,
}))(NewUserPage);

export default ConnectedNewUserPage;
